// src/pages/AmdPage.js
import React, { useEffect, useState, useContext } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, IconButton, Tooltip, Grid, FormControlLabel, Checkbox
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, GraphicEq as GraphicEqIcon } from '@mui/icons-material';
import { Controller, useForm } from 'react-hook-form';
import { ApiContext } from '../contexts/ApiContext';
import { useNotification } from '../contexts/NotificationContext';
import { usePermissions } from '../hooks/usePermissions';

const defaults = {
  id: undefined,
  amdName: '',
  initialSilence: 3000,
  greeting: 2000,
  afterGreetingSilence: 1250,
  totalAnalysis: 3500,
  miniumWordLength: 100,
  betweenWordSilence: 50,
  maximumNumberOfWords: 5,
  silenceThreshold: 256,
  maximumWordLength: 2000,
  initialbeep: true,
};

export default function AmdPage() {
  const { amd } = useContext(ApiContext);
  const { showSuccess, showError } = useNotification();
  const { canAction } = usePermissions();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const { control, handleSubmit, reset, formState:{errors} } = useForm({ defaultValues: defaults });

  const load = async () => {
    const data = await amd.getAll();
    setRows(Array.isArray(data) ? data : []);
  };
  useEffect(() => { load(); }, []);


  const openDialog = (row=null) => {
    setEditing(row);
    reset(row ? { ...defaults, ...row, initialbeep: !!row.initialbeep } : defaults);
    setOpen(true);
  };
  const closeDialog = () => { setOpen(false); setEditing(null); };

  const onSubmit = async (values) => {
    const payload = { ...values, initialbeep: values.initialbeep ? 1 : 0 };
    try {
      if (values.id) await amd.update(values.id, payload);
      else await amd.create(payload);
      showSuccess(`Configuración AMD ${values.id ? 'actualizada' : 'creada'} correctamente`);
      closeDialog();
      load();
    } catch (e) {
      showError(e?.message || 'Error al guardar configuración AMD');
      console.error(e);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`¿Eliminar configuración AMD "${row.amdName}"?`)) return;
    try {
      await amd.remove(row.id);
      showSuccess('Configuración AMD eliminada');
      load();
    } catch (e) {
      showError(e?.message || 'Error al eliminar configuración AMD');
    }
  };

  const Num = ({ name, label }) => (
    <Controller
      name={name}
      control={control}
      rules={{ required: 'Requerido', min: { value: 0, message: '>= 0' } }}
      render={({ field }) => (
        <TextField {...field} type="number" label={label} fullWidth
          error={!!errors[name]} helperText={errors[name]?.message} />
      )}
    />
  );

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>Configuraciones AMD</Typography>
        {canAction('create_amd') && (
          <Button startIcon={<AddIcon />} variant="contained" onClick={() => openDialog(null)}>
            Nueva configuración
          </Button>
        )}
      </Box>

      <Card sx={{ mb:2 }}>
        <CardContent sx={{ display:'flex', gap:2, alignItems:'center' }}>
          <GraphicEqIcon fontSize="small" />
          <Typography variant="body2">Total: {rows.length}</Typography>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Nombre</TableCell>
              <TableCell>Inicial (ms)</TableCell>
              <TableCell>Saludo (ms)</TableCell>
              <TableCell>Silencio pos-saludo</TableCell>
              <TableCell>Total análisis</TableCell>
              <TableCell>Min palabra</TableCell>
              <TableCell>Silencio entre palabras</TableCell>
              <TableCell>Máx palabras</TableCell>
              <TableCell>Ruido</TableCell>
              <TableCell>Máx palabra</TableCell>
              <TableCell>Beep</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.id} hover>
                <TableCell>{r.amdName}</TableCell>
                <TableCell>{r.initialSilence}</TableCell>
                <TableCell>{r.greeting}</TableCell>
                <TableCell>{r.afterGreetingSilence}</TableCell>
                <TableCell>{r.totalAnalysis}</TableCell>
                <TableCell>{r.miniumWordLength}</TableCell>
                <TableCell>{r.betweenWordSilence}</TableCell>
                <TableCell>{r.maximumNumberOfWords}</TableCell>
                <TableCell>{r.silenceThreshold}</TableCell>
                <TableCell>{r.maximumWordLength}</TableCell>
                <TableCell>{r.initialbeep ? 'sí' : 'no'}</TableCell>
                <TableCell align="right">
                  {canAction('edit_amd') && (
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => openDialog(r)}><EditIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                  {canAction('delete_amd') && (
                    <Tooltip title="Eliminar">
                      <IconButton size="small" onClick={() => onDelete(r)}><DeleteIcon fontSize="small" /></IconButton>
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={12} align="center">No hay configuraciones AMD.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editing ? 'Editar configuración AMD' : 'Nueva configuración AMD'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Controller
                name="amdName"
                control={control}
                rules={{ required: 'Requerido', maxLength: { value: 25, message: 'máx 25 caracteres' } }}
                render={({ field }) => (
                  <TextField {...field} label="Nombre Configuración AMD" fullWidth
                    error={!!errors.amdName} helperText={errors.amdName?.message} />
                )}
              />
            </Grid>

            <Grid item xs={12} md={6}><Num name="initialSilence" label="Silencio Inicial (ms)" /></Grid>
            <Grid item xs={12} md={6}><Num name="greeting" label="Duración Saludo (ms)" /></Grid>
            <Grid item xs={12} md={6}><Num name="afterGreetingSilence" label="Silencio después del saludo (ms)" /></Grid>
            <Grid item xs={12} md={6}><Num name="totalAnalysis" label="Total análisis (ms)" /></Grid>
            <Grid item xs={12} md={6}><Num name="miniumWordLength" label="Duración mínima de una palabra (ms)" /></Grid>
            <Grid item xs={12} md={6}><Num name="betweenWordSilence" label="Silencio entre palabras (ms)" /></Grid>
            <Grid item xs={12} md={6}><Num name="maximumNumberOfWords" label="Número máximo de palabras" /></Grid>
            <Grid item xs={12} md={6}><Num name="silenceThreshold" label="Nivel de ruido (threshold)" /></Grid>
            <Grid item xs={12} md={6}><Num name="maximumWordLength" label="Duración máx de una palabra (ms)" /></Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Controller
                    name="initialbeep"
                    control={control}
                    render={({ field }) => <Checkbox {...field} checked={!!field.value} />}
                  />
                }
                label="Beep inicial"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancelar</Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)}>
            {editing ? 'Guardar cambios' : 'Crear'}
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
